/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import MsDyn365, { RichTextComponent } from '@msdyn365-commerce/core';
import {
    ContactInformationType,
    DeliveryOption,
    IDictionary,
    OrgUnitContact,
    OrgUnitLocation,
    StoreHours
} from '@msdyn365-commerce/retail-proxy';
import { IStoreInfo } from '@msdyn365-commerce-modules/bopis-utilities';
import { GetProductUnitOfMeasureInput, getProductUnitsOfMeasureAction } from '../../../actions/getproductunitsofmeasure.action';
import {
    ArrayExtensions,
    GetOrgUnitLocationsByAreaInput,
    getOrgUnitLocationsByArea,
    StringExtensions
} from '@msdyn365-commerce-modules/retail-actions';

import {
    buildStoreHours,
    IHoursDisplayInfo,
    ILocationHours,
    IStoreSelectorFoundLocationsMessage,
    IStoreSelectorLocationLineItemProps,
    IStoreSelectorLocationLineItemResources,
    IStoreSelectorViewProps,
    secondsToTime
} from '@msdyn365-commerce-modules/store-selector';
import { ISelectedStoreViewProps } from '@msdyn365-commerce-modules/store-selector/src/modules/store-selector/components/selected-store/selected-store';
import {
    getPayloadObject,
    getTelemetryAttributes,
    getTelemetryObject,
    IPayLoad,
    isMobile,
    Module,
    Node,
    TelemetryConstant,
    VariantType
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { IStoreSelectorProps, IStoreSelectorResources } from '../definition-extensions/store-selector.ext.props.autogenerated';
import { PaymentStateShareInput } from '../../../actions/payment-state-share.action';
import { IStoreSelectorData } from '../../../dataTypes/store-selector.data';
import { addPreferredStoreAsync } from '../../../custom-actions/DataActionExtension.g';

type UOMAvailableStore = {
    PickupStore: string;
};

/**
 * PickUpMethodComponent View Props Interface.
 */
export interface IPickupComponent extends IStoreSelectorLocationLineItemProps {
    pickUpStatesProps: IStoreSelectorViewProps;
}

export interface IPickUpButtonComponent {
    attributes: IDictionary<string> | undefined;
    onPickupInStoreClickHandler?(): void;
}

/**
 * Found Locations Message component in Store Selector.
 * @param props - The component props.
 * @returns The JSX Element.
 */
const StoreSelectorFoundLocationsMessageComponent: React.FC<IStoreSelectorFoundLocationsMessage> = props => {
    const { locations, searchRadius, resources, isSeeAllStore, preferredDistanceUnit } = props;
    if (locations) {
        const storeCountMessage =
            preferredDistanceUnit === resources.milesShortText ? resources.storeCountMessage : resources.storeCountMessageInKm;

        return (
            <RichTextComponent
                className='ms-store-select__found-locations__text'
                text={
                    isSeeAllStore
                        ? resources.storeAllCountMessage.replace(
                              '{count}',
                              `<span class='ms-store-select__found-locations__count'>${locations.length.toString()}</span>`
                          )
                        : storeCountMessage
                              .replace(
                                  '{count}',
                                  `<span class='ms-store-select__found-locations__count'>${locations.length.toString()}</span>`
                              )
                              .replace(
                                  '{radius}',
                                  `<span class='ms-store-select__found-locations__radius'>${searchRadius.toString()}</span>`
                              )
                }
            />
        );
    }

    return null;
};

/**
 * Build distance string.
 * @param distance - The distance.
 * @param preferredDistanceUnit - The preferred distance unit.
 * @param resources - The module resources.
 * @param isLocationDisabled - True if location is disabled.
 * @returns The distance string.
 */
const buildDistanceString = (
    distance: number | undefined,
    preferredDistanceUnit: string,
    resources: IStoreSelectorResources & IStoreSelectorResources,
    isLocationDisabled?: boolean
): string | undefined => {
    const closeDistance: number = 1;
    if (distance === undefined || isLocationDisabled) {
        return undefined;
    } else if (distance < closeDistance) {
        const closeDistanceString: string = resources.closeDistanceString;
        return closeDistanceString.replace('{preferredDistanceUnit}', preferredDistanceUnit);
    }
    const distanceAsString: string = resources.distanceString;
    return distanceAsString
        .replace('{preferredDistanceUnit}', preferredDistanceUnit)
        .replace('{distance}', Math.floor(distance).toString());
};

/**
 * Extract phone number.
 * @param contacts - Contacts array.
 * @returns Phone number.
 */
const extractPhoneNumber = (contacts: OrgUnitContact[] | undefined): string | undefined => {
    if (!contacts) {
        return undefined;
    }

    const allPhoneContacts = contacts.filter(contact => contact.ContactTypeValue === ContactInformationType.Phone && !contact.IsPrivate);

    if (ArrayExtensions.hasElements(allPhoneContacts)) {
        const primaryPhoneContact = allPhoneContacts.find(contact => contact.IsPrimary);

        if (primaryPhoneContact) {
            return primaryPhoneContact.Locator;
        }

        return allPhoneContacts[0].Locator;
    }

    return undefined;
};

/**
 * Render Store Hours Day.
 * @param displayTime - Store Hours Information.
 * @param storeHoursDayPeriodString - The string for store hours day period.
 * @param storeHoursDayString - The string for store hours day period.
 * @returns JSX element with store hours day.
 */
const buildStoreHoursDay = (displayTime: IHoursDisplayInfo, storeHoursDayPeriodString: string, storeHoursDayString: string) => {
    return (
        <RichTextComponent
            className='ms-store-select__store-hours-day'
            text={
                displayTime.endDayShort
                    ? storeHoursDayPeriodString
                          .replace('{startDay}', `<abbr title=${displayTime.startDayFull}>${displayTime.startDayShort}</abbr>`)
                          .replace(
                              '{endDay}',
                              `<abbr title=${displayTime.endDayFull ? displayTime.endDayFull : ''}>${displayTime.endDayShort}</abbr>`
                          )
                    : storeHoursDayString.replace(
                          '{startDay}',
                          `<abbr title=${displayTime.startDayFull}>${displayTime.startDayShort}</abbr>`
                      )
            }
        />
    );
};

/**
 * Render Store Hours.
 * @param storeHours - Store hours.
 * @param storeHoursList - Store hours list.
 * @param locationOpen - Location open.
 * @param resources - The module resources.
 * @returns JSX element with store hours.
 */
const renderStoreHours = (
    storeHours: StoreHours | undefined,
    storeHoursList: IHoursDisplayInfo[] | undefined,
    locationOpen: ILocationHours | undefined,
    resources: IStoreSelectorResources & IStoreSelectorResources
): JSX.Element | null => {
    // First, use the store hours. This will allow listing day by day hours
    if (storeHours?.RegularStoreHours) {
        return (
            <div className='ms-store-select__store-hours-details-group'>
                {storeHoursList?.map(displayTime => (
                    <div className='ms-store-select__store-hours-details' key={JSON.stringify(displayTime)}>
                        {buildStoreHoursDay(displayTime, resources.storeHoursDayPeriod, resources.storeHoursDay)}
                        <div className='ms-store-select__store-hours-time'>{displayTime.timeRange}</div>
                    </div>
                ))}
            </div>
        );
    }

    // If that fails, fall back to using the OpenFrom and OpenTo properties on the location
    const storeOpenFrom: string = locationOpen?.openFrom ? locationOpen.openFrom : '';
    const storeOpenTo: string = locationOpen?.openTo ? locationOpen.openTo : '';
    return (
        <div className='ms-store-select__store-hours-details'>
            {resources.storeHoursDayPeriod.replace('{startDay}', storeOpenFrom).replace('{endDay}', storeOpenTo)}
        </div>
    );
};

/**
 * Match delivery options.
 * @param productDeliveryOptions - The list of product delivery options.
 * @param storePickUpOptionList - The list of store pick up options.
 * @returns Matched delivery option.
 */
const matchDeliveryOptions = (
    productDeliveryOptions: DeliveryOption[] | undefined,
    storePickUpOptionList: DeliveryOption[] | undefined
): DeliveryOption[] => {
    if (!productDeliveryOptions) {
        return [] as DeliveryOption[];
    }
    return productDeliveryOptions
        .map(productDeliveryOption => storePickUpOptionList?.find(delivery => delivery.Code === productDeliveryOption.Code))
        .filter(productDeliveryOption => productDeliveryOption) as DeliveryOption[];
};

/**
 * PickUpMethod Resources Interface.
 */
export interface IPickUpButtonResources {
    pickupInStoreAriaLabel: string;
    pickupInStoreButtonText: string;
    pickupDeliveryOptionErrorMessage: string;
}

/**
 * PickUpMethod Resources Interface.
 */
export interface IPickUpMethodResources {
    selectStoreText: string;
    pickUpMethodLabel: string;
    pickUpOptionPlaceholder: string;
    pickupOptionDropdownError: string;
    pickupButtonAriaLabel: string;
}

/**
 * PickUp method dropdown component.
 * @param props - The view props.
 * @returns JSX Element.
 */
const PickUpMethodDropdownComponent = (props: IPickupComponent & IPickUpMethodResources) => {
    const {
        handlers,
        location,
        selectStoreText,
        pickUpMethodLabel,
        pickupButtonAriaLabel,
        productPickUpOptionList,
        storePickUpOptionList
    } = props;

    const deliveryOptionList = matchDeliveryOptions(productPickUpOptionList, storePickUpOptionList);

    // Assume there is only one delivery option in the list
    const defaultDeliveryOption = deliveryOptionList[0];
    const [selectedStorePickUpOption] = React.useState(defaultDeliveryOption?.Code || '');
    const [currentStorePickUpOption] = React.useState(defaultDeliveryOption?.Description || '');
    const [isError, setError] = React.useState(false);

    /**
     * Method to handle the store pickup option submit.
     */
    const onClickHandler = React.useCallback(() => {
        if (!StringExtensions.isNullOrEmpty(selectedStorePickUpOption)) {
            handlers.onSelected(location, selectedStorePickUpOption);
        } else {
            setError(true);
        }
    }, [location, selectedStorePickUpOption, handlers]);

    return (
        <div className='ms-store-select__location-line-pickup' data-selected={!!selectedStorePickUpOption} data-error={isError}>
            <div className='ms-store-select__location-line-pickup-label'>{pickUpMethodLabel}</div>
            {defaultDeliveryOption ? (
                <div className='ms-store-select__location-line-pickup-list-container'>
                    <div className='ms-store-select__location-line-pickup-selected'>{currentStorePickUpOption}</div>
                </div>
            ) : (
                <div className='ms-store-select__location-line-pickup-list-error'>No pickup options available.</div>
            )}
            <button className='ms-store-select__location-line-pickup-menu' aria-label={pickupButtonAriaLabel} onClick={onClickHandler}>
                {selectStoreText}
            </button>
        </div>
    );
};

/**
 * PickUp button component.
 * @param props - The view props.
 * @returns JSX Element.
 */
const PickUpButtonComponent = (props: IPickUpButtonComponent & IPickUpButtonResources) => {
    const { pickupInStoreAriaLabel, pickupInStoreButtonText, pickupDeliveryOptionErrorMessage, attributes } = props;
    const [isError, setError] = React.useState(false);
    const onPickupButtonClickHandler = () => () => {
        setError(true);
    };

    return (
        <>
            <button
                className='ms-store-select__location-line-select-store'
                aria-label={pickupInStoreAriaLabel}
                onClick={onPickupButtonClickHandler()}
                {...attributes}
            >
                {pickupInStoreButtonText}
            </button>
            <div className='ms-store-select__location-line-pickup-button-error' data-pickup-error={isError}>
                {pickupDeliveryOptionErrorMessage}
            </div>
        </>
    );
};

/**
 * Method to render store location line item.
 * @param locationLineItem - The Location Line item view props.
 * @param props - The view props.
 * @returns JSX Element.
 */
const renderStoreLocationLineItem = (
    locationLineItem: IStoreSelectorLocationLineItemProps,
    props: IStoreSelectorViewProps & IStoreSelectorProps<IStoreSelectorResources & IStoreSelectorData>,
    reloadAfterUpdating: boolean
) => {
    const {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from store-selector.tsx file
        hideStockStatus,
        index,
        isInStock,
        preferredStoreLocationId,
        isPreferredStoreEnabled,
        location,
        resources,
        storeHours,
        storeHoursList,
        stockStatusLabel,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from store-selector.tsx file
        storeLocatorView,
        telemetryContent
    } = locationLineItem;
    const storeName = location.OrgUnitName;
    const distanceAsString: string | undefined = buildDistanceString(
        location.Distance,
        locationLineItem.preferredDistanceUnit,
        props.resources,
        locationLineItem.isLocationDisabled
    );
    const storeAddress: string | undefined = location.Address;
    const phoneNumber: string | undefined = extractPhoneNumber(location.Contacts);

    const resourceLabel = isInStock ? resources.inStock : resources.outOfStock;
    const stockStatus = stockStatusLabel ? stockStatusLabel : resourceLabel;
    const stockText: string | undefined = hideStockStatus ? undefined : stockStatus;

    const payLoad: IPayLoad = getPayloadObject('click', telemetryContent!, TelemetryConstant.PickupInStore);

    /**
     * Method to handle the change for preferred store.
     * @returns Void.
     */
    const onSetPreferredStoreClickHandler = async () => {
        await addPreferredStoreAsync(
            { callerContext: props.context.actionContext },
            {
                CustomerAccountNumber: props.context.request.user.customerAccountNumber ?? '',
                StoreId: locationLineItem.location.OrgUnitNumber,
                StoreName: locationLineItem.location.OrgUnitName,
                Latitude: locationLineItem.location.Latitude ?? 0,
                Longitude: locationLineItem.location.Longitude ?? 0,
                ExtensionProperties: locationLineItem.location?.ExtensionProperties
            }
        );
        if (!reloadAfterUpdating) {
            window.location.reload();
        }
        locationLineItem.handlers.onSetAsPreferredStore(locationLineItem.location.OrgUnitNumber);
    };

    /**
     * Method to handle the removal of the preferred store.
     * @returns Void.
     */
    const onRemovePreferredStoreClickHandler = async (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.stopPropagation();
        // locationLineItem.handlers.onRemovePreferredStore();
        // window.location.reload();
        // await props.data.storeSelectorStateManager.result?.setSelectedStoreLocationId(undefined);
    };
    const isPreferredStore = preferredStoreLocationId === location.OrgUnitNumber;
    payLoad.contentAction.etext = TelemetryConstant.PreferredStore;
    const preferredStoreAttribute = getTelemetryAttributes(telemetryContent!, payLoad);
    payLoad.contentAction.etext = TelemetryConstant.RemovePreferredStore;
    const removePreferredStoreAttribute = getTelemetryAttributes(telemetryContent!, payLoad);

    return (
        <div className='ms-store-select__location-line-item'>
            <div className='ms-store-select__location-line-item-header'>
                {storeLocatorView && <span className='ms-store-select__location-line-item-store-index'>{index}</span>}
                <span className='ms-store-select__location-line-item-store-name'>{storeName}</span>
                {distanceAsString ? <span className='ms-store-select__location-line-item-store-distance'>{distanceAsString}</span> : false}
            </div>
            <div className='ms-store-select__location-line-item-content'>
                <div className='ms-store-select__location-line-item-contact-info'>
                    <p className='ms-store-select__location-line-header'>{locationLineItem.resources.contactInfoHeader}</p>
                    {storeAddress && (
                        <div className='ms-store-select__location-line-shop-address-section' aria-label={storeAddress} role='button'>
                            <span
                                className='msi msi-map-marker-alt ms-store-select__location-line-shop-address-glyph-icon'
                                aria-hidden='true'
                            />
                            <span className='ms-store-select__location-line-shop-address-text' aria-hidden='true'>
                                {storeAddress}
                            </span>
                        </div>
                    )}
                    {phoneNumber && (
                        <div className='ms-store-select__location-line-shop-phone-section'>
                            <span className='msi msi-phone ms-store-select__location-line-shop-phone-icon' />
                            <span className='ms-store-select__location-line-shop-phone-text'>{phoneNumber}</span>
                        </div>
                    )}
                </div>
                <div className='ms-store-select__location-line-item-store-hours'>
                    <p className='ms-store-select__location-line-header'>{locationLineItem.resources.storeHoursHeader}</p>
                    {renderStoreHours(storeHours, storeHoursList, locationLineItem.locationOpen, props.resources)}
                </div>
                {!storeLocatorView && !hideStockStatus && (
                    <div className='ms-store-select__location-line-item-store-availability'>
                        <p className='ms-store-select__location-line-header'>{resources.availabilityInfoHeader}</p>
                        {stockText && <p className='ms-store-select__location-line-stock-status'>{stockText}</p>}
                    </div>
                )}
                {isPreferredStoreEnabled &&
                    (isPreferredStore ? (
                        <button
                            role='checkbox'
                            className='ms-store-select__location-line-item-preferred-store'
                            aria-live='polite'
                            aria-checked='true'
                            aria-label={resources.preferredStoreAriaLabel}
                            onClick={onRemovePreferredStoreClickHandler}
                            {...removePreferredStoreAttribute}
                        >
                            {resources.preferredStoreText}
                        </button>
                    ) : (
                        <button
                            role='checkbox'
                            className='ms-store-select__location-line-item-set-as-preferred-store'
                            aria-live='polite'
                            aria-checked='false'
                            aria-label={resources.setAsPreferredStoreTextAriaLabel}
                            {...preferredStoreAttribute}
                            onClick={onSetPreferredStoreClickHandler}
                        >
                            {resources.setAsPreferredStoreText}
                        </button>
                    ))}
            </div>
        </div>
    );
};

/**
 * Render Store Location.
 * @param location - The location props.
 * @param index - The location index.
 * @param props - The view props.
 * @param selectedLocationReference - The ref.
 * @returns The JSX Element.
 */
const renderStoreLocation = (
    location: IStoreSelectorLocationLineItemProps,
    index: number,
    props: IStoreSelectorViewProps & IStoreSelectorProps<IStoreSelectorResources & IStoreSelectorData>,
    selectedLocationReference: React.RefObject<HTMLDivElement>,
    reloadAfterUpdating: boolean
): JSX.Element | undefined => {
    const {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from store-selector.tsx file
        hideStockStatus,
        storeLocatorView,
        isCurrentLocation,
        isInStock,
        productPickUpOptionList,
        resources,
        storePickUpOptionList,
        telemetryContent
    } = location;

    const orgUnitLocation = location.location;
    const selectedStoreLocationId = location.selectedStoreLocationId;
    const defaultAriaSetsize = 0;
    const canSelectLocation = hideStockStatus || isInStock;
    const storeName = location.location.OrgUnitName;
    const pickupInStoreAriaLabel: string = resources.selectLocationAriaLabelFormat.replace('{StoreName}', storeName ?? '');
    const pickupInStoreButtonText: string = resources.selectLocation;
    const pickUpOptionDropdownPlaceholderText: string = props.resources.pickupOptionDropdownPlaceholderText;

    const payLoad: IPayLoad = getPayloadObject('click', telemetryContent!, TelemetryConstant.PickupInStore);
    const attributes = getTelemetryAttributes(telemetryContent!, payLoad);
    const deliveryOptionList = matchDeliveryOptions(productPickUpOptionList, storePickUpOptionList);
    const selectStoreText = props.resources.selectStoreText;
    const pickUpMethodLabel = props.resources.pickUpMethodLabel;

    /**
     * Method to handle the pick up in store.
     * @returns Void.
     */
    const onPickupInStoreClickHandler = () => () => {
        location.handlers.onSelected(location.location);
    };

    /**
     * Render Store Locations.
     * @param unitLocation - The org unit location.
     * @returns Handler for onClick.
     */
    const onClickHandler = (unitLocation: OrgUnitLocation | undefined) => () => {
        location.onClick(unitLocation?.OrgUnitNumber);
    };

    const canSelectPickUpMethod = !ArrayExtensions.hasElements(deliveryOptionList) ? (
        <PickUpButtonComponent
            attributes={attributes}
            pickupInStoreAriaLabel={pickupInStoreAriaLabel}
            pickupInStoreButtonText={pickupInStoreButtonText}
            pickupDeliveryOptionErrorMessage={props.resources.pickupDeliveryOptionErrorMessage}
            onPickupInStoreClickHandler={onPickupInStoreClickHandler}
        />
    ) : (
        <PickUpMethodDropdownComponent
            {...location}
            pickUpStatesProps={props}
            selectStoreText={selectStoreText}
            pickUpMethodLabel={pickUpMethodLabel}
            pickUpOptionPlaceholder={pickUpOptionDropdownPlaceholderText}
            pickupOptionDropdownError={props.resources.pickupOptionDropdownErrorText}
            pickupButtonAriaLabel={pickupInStoreAriaLabel}
        />
    );
    const pickUpMethodSelection = canSelectLocation ? canSelectPickUpMethod : false;

    return (
        <div
            className={classnames('ms-store-select__location-wrapper', {
                selected: selectedStoreLocationId === orgUnitLocation.OrgUnitNumber && !props.isMobileView
            })}
        >
            <div
                className={classnames('ms-store-select__location', { selected: selectedStoreLocationId === orgUnitLocation.OrgUnitNumber })}
                key={index}
                role='listitem'
                aria-setsize={ArrayExtensions.hasElements(props.locationListProps) ? props.locationListProps.length : defaultAriaSetsize}
                aria-posinset={Number.parseInt(location.index, 10)}
                onClick={onClickHandler(orgUnitLocation)}
                onKeyDown={onClickHandler(orgUnitLocation)}
                ref={selectedStoreLocationId === orgUnitLocation.OrgUnitNumber ? selectedLocationReference : undefined}
            >
                {renderStoreLocationLineItem(location, props, reloadAfterUpdating)}
            </div>
            {!storeLocatorView &&
                (isCurrentLocation ? (
                    <p className='ms-store-select__location-line-current-location'>{resources.currentLocation}</p>
                ) : (
                    pickUpMethodSelection
                ))}
        </div>
    );
};

/**
 * Render Store Locations.
 * @param props - The view props.
 * @param selectedLocationReference - The ref.
 * @returns The JSX Element.
 */
const renderStoreLocations = (
    props: IStoreSelectorViewProps & IStoreSelectorProps<IStoreSelectorResources & IStoreSelectorData>,
    selectedLocationReference: React.RefObject<HTMLDivElement>,
    reloadAfterUpdating: boolean
) => {
    return (
        <div
            className={classnames('ms-store-select__locations', {
                show: props.data.storeSelectorStateManager.result?.listMapViewState.displayList
            })}
            role='list'
        >
            {props.locationListProps?.map((store, index) => {
                return renderStoreLocation(store, index, props, selectedLocationReference, reloadAfterUpdating);
            })}
        </div>
    );
};

/**
 * Method to map resources with Location Item.
 * @param resources - List of strings for module.
 * @returns Resources for StoreSelector location line.
 */
const mapResources = (resources: IStoreSelectorResources): IStoreSelectorLocationLineItemResources => {
    return {
        contactInfoHeader: resources.contactText,
        storeHoursHeader: resources.timeText,
        availabilityInfoHeader: resources.availabilityText,

        closedText: resources.storeHoursClosedText,
        outOfStock: resources.outOfStockText,
        inStock: resources.inStockText,

        currentLocation: resources.selectedStoreLocationText,
        selectLocation: resources.selectStoreText,
        selectLocationAriaLabelFormat: resources.selectStoreAriaFormatText,
        setAsPreferredStoreText: resources.setAsPreferredStoreText,
        setAsPreferredStoreTextAriaLabel: resources.setAsPreferredStoreTextAriaLabel,
        preferredStoreText: resources.preferredStoreText,
        preferredStoreAriaLabel: resources.preferredStoreAriaLabel,

        days: {
            monday: resources.storeHoursMondayText,
            tuesday: resources.storeHoursTuesdayText,
            wednesday: resources.storeHoursWednesdayText,
            thursday: resources.storeHoursThursdayText,
            friday: resources.storeHoursFridayText,
            saturday: resources.storeHoursSaturdayText,
            sunday: resources.storeHoursSundayText,
            mondayFull: resources.storeHoursMondayFullText,
            tuesdayFull: resources.storeHoursTuesdayFullText,
            wednesdayFull: resources.storeHoursWednesdayFullText,
            thursdayFull: resources.storeHoursThursdayFullText,
            fridayFull: resources.storeHoursFridayFullText,
            saturdayFull: resources.storeHoursSaturdayFullText,
            sundayFull: resources.storeHoursSundayFullText
        }
    };
};

/**
 * Render the selected store.
 * @param selectedLocationProps -- Selected store view props.
 * @param storeSelectorViewProps -- Store selector view props.
 * @returns -- HTML.
 */
export const renderSelectedStore = (
    selectedLocationProps: ISelectedStoreViewProps,
    storeSelectorViewProps: IStoreSelectorViewProps & IStoreSelectorProps<IStoreSelectorResources>
): JSX.Element => {
    const {
        storeDetailsMain,
        storeHeader,
        storeName,
        storeDetailsCloseButton,
        storeDistance,
        storeDetailsAvailabilityContainer,
        productAvailability,
        stockStatus,
        isPreferredStore,
        preferredStoreButton,
        removePreferredStoreButton,
        selectedStore
    } = selectedLocationProps;

    const storeAddress: string | undefined = selectedStore?.OrgUnitAvailability?.OrgUnitLocation?.Address;
    const phoneNumber: string | undefined = extractPhoneNumber(selectedStore?.OrgUnitAvailability?.OrgUnitLocation?.Contacts);
    const storeHoursList = selectedStore?.StoreHours?.RegularStoreHours
        ? buildStoreHours(selectedStore.StoreHours.RegularStoreHours, mapResources(storeSelectorViewProps.resources))
        : [];
    const storeOpenFrom: string | undefined = secondsToTime(selectedStore?.OrgUnitAvailability?.OrgUnitLocation?.OpenFrom);
    const storeOpenTo: string | undefined = secondsToTime(selectedStore?.OrgUnitAvailability?.OrgUnitLocation?.OpenTo);
    const locationOpen = { openFrom: storeOpenFrom, openTo: storeOpenTo };
    const storeNameValue = selectedStore?.OrgUnitAvailability?.OrgUnitLocation?.OrgUnitName;
    const pickupInStoreAriaLabel: string = selectedLocationProps.resources.selectLocationAriaLabelFormat.replace(
        '{StoreName}',
        storeNameValue ?? ''
    );
    const telemetryContent = getTelemetryObject(
        storeSelectorViewProps.context.request.telemetryPageName!,
        storeSelectorViewProps.friendlyName,
        storeSelectorViewProps.telemetry
    );
    const payLoad = getPayloadObject('click', telemetryContent, TelemetryConstant.PickupInStore);
    const attributes = getTelemetryAttributes(telemetryContent, payLoad);
    const pickupInStoreButtonText: string = selectedLocationProps.resources.selectLocation;
    const selectStoreText = storeSelectorViewProps.resources.selectStoreText;
    const pickUpMethodLabel = storeSelectorViewProps.resources.pickUpMethodLabel;
    const pickUpOptionDropdownPlaceholderText: string = storeSelectorViewProps.resources.pickupOptionDropdownPlaceholderText;
    const orgUnitNumber = selectedStore?.OrgUnitAvailability?.OrgUnitLocation!.OrgUnitNumber;
    const productPickUpOptionList = storeSelectorViewProps.data.storeSelectorStateManager.result?.context?.deliveryOptions?.DeliveryOptions;
    const deliveryOptionList = matchDeliveryOptions(productPickUpOptionList, selectedLocationProps.deliveryOptions);
    const selectedLocation = storeSelectorViewProps.locationListProps?.find(location => location.location.OrgUnitNumber === orgUnitNumber);

    /**
     * Method to handle the pick up in store.
     * @returns Void.
     */
    const onPickupInStoreClickHandler = () => () => {
        selectedLocationProps.handlers.onSelected(selectedStore!.OrgUnitAvailability!.OrgUnitLocation!);
    };
    const canSelectPickUpMethod = !ArrayExtensions.hasElements(deliveryOptionList) ? (
        <PickUpButtonComponent
            attributes={attributes}
            pickupInStoreAriaLabel={pickupInStoreAriaLabel}
            pickupInStoreButtonText={pickupInStoreButtonText}
            pickupDeliveryOptionErrorMessage={storeSelectorViewProps.resources.pickupDeliveryOptionErrorMessage}
            onPickupInStoreClickHandler={onPickupInStoreClickHandler}
        />
    ) : (
        selectedLocation && (
            <PickUpMethodDropdownComponent
                {...selectedLocation}
                pickUpStatesProps={storeSelectorViewProps}
                selectStoreText={selectStoreText}
                pickUpMethodLabel={pickUpMethodLabel}
                pickUpOptionPlaceholder={pickUpOptionDropdownPlaceholderText}
                pickupOptionDropdownError={storeSelectorViewProps.resources.pickupOptionDropdownErrorText}
                pickupButtonAriaLabel={pickupInStoreAriaLabel}
            />
        )
    );

    return (
        <Node {...storeDetailsMain}>
            <Node {...storeHeader}>
                {storeDetailsCloseButton}
                {storeName}
                {storeDistance}
            </Node>
            <Node {...storeDetailsAvailabilityContainer}>
                <div className='ms-store-select__location-line-item-contact-info'>
                    <p className='ms-store-select__location-line-header'>{storeSelectorViewProps.resources.contactText}</p>
                    {storeAddress && (
                        <div className='ms-store-select__location-line-shop-address-section' aria-label={storeAddress} role='button'>
                            <span
                                className='msi msi-map-marker-alt ms-store-select__location-line-shop-address-glyph-icon'
                                aria-hidden='true'
                            />
                            <span className='ms-store-select__location-line-shop-address-text' aria-hidden='true'>
                                {storeAddress}
                            </span>
                        </div>
                    )}
                    {phoneNumber && (
                        <div className='ms-store-select__location-line-shop-phone-section'>
                            <span className='msi msi-phone ms-store-select__location-line-shop-phone-icon' />
                            <span className='ms-store-select__location-line-shop-phone-text'>{phoneNumber}</span>
                        </div>
                    )}
                </div>
                <div className='ms-store-select__location-line-item-store-hours'>
                    <p className='ms-store-select__location-line-header'>{storeSelectorViewProps.resources.timeText}</p>
                    {renderStoreHours(selectedStore?.StoreHours, storeHoursList, locationOpen, storeSelectorViewProps.resources)}
                </div>
                {productAvailability && (
                    <div className='ms-store-select__location-line-item-store-availability'>
                        <p className='ms-store-select__location-line-header'>{productAvailability}</p>
                        {stockStatus && <p className='ms-store-select__location-line-stock-status'>{stockStatus}</p>}
                    </div>
                )}
            </Node>
            {isPreferredStore ? preferredStoreButton : removePreferredStoreButton}
            {canSelectPickUpMethod}
        </Node>
    );
};

/**
 * Store Selector View.
 * @param props - The view props.
 * @returns The JSX Element.
 */
const StoreSelectorViewComponent: React.FC<IStoreSelectorViewProps &
    IStoreSelectorProps<IStoreSelectorResources & IStoreSelectorData>> = props => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Store-Selector.tsx file
    const {
        BodyContainerProps,
        storeResultContainerProps,
        FooterContainerProps,
        HeaderContainerProps,
        HeaderProps,
        ModuleProps,
        locationsMessage,
        search,
        state,
        spinner,
        maps,
        locationsList,
        noLocationsMessage,
        terms,
        storeDetailsModal,
        selectedLocation,
        bodyWrapperProps,
        data,
        context,
        config,
        resources
    } = props;
    const selectedLocationReference = React.useRef(null);
    const storeContainerRef = React.useRef<HTMLDivElement>(null);
    const [isDesktopPort, setIsDesktopPort] = React.useState<boolean>(false);
    const [fetched, setFetched] = React.useState<boolean>(false);
    const [reloadAfterUpdating, setIsloadAfterUpdating] = React.useState<boolean>(false);
    const [isUpdatingCartLines, setIsUpdatingCartLines] = React.useState<boolean>(false);

    const isDesktopViewport = React.useCallback(() => {
        const portView = isMobile({ variant: VariantType.Browser, context: context.request });
        const isDesktopPorts = portView === 'md' || portView === 'xl' || portView === 'lg';
        setIsDesktopPort(isDesktopPorts);
    }, [context]);

    React.useEffect(() => {
        if (props.locationsList) {
            /**
             * Method to handle scrolling.
             */
            const scrollIntoView = () => {
                const selectedLocationDiv = (selectedLocationReference as React.RefObject<HTMLDivElement>).current;
                if (selectedLocationDiv !== null) {
                    const offset = selectedLocationDiv.getBoundingClientRect();
                    const parent = selectedLocationDiv.parentElement?.parentElement;
                    if (parent && offset) {
                        const totalHeight = parent.scrollTop + offset.top;
                        parent.scrollTop = totalHeight - parent.clientHeight;
                    }
                }
            };
            scrollIntoView();
        }
    }, [props.locationsList]);

    React.useEffect(() => {
        if (MsDyn365.isBrowser) {
            window.addEventListener('resize', isDesktopViewport);
        }
        isDesktopViewport();
    }, [isDesktopViewport]);

    React.useEffect(() => {
        if (props.data.storeSelectorStateManager?.result?.preferredStore !== undefined) {
            props.context.actionContext.update(new PaymentStateShareInput(), {
                isStoreSelected: true,
                isCODSelected: true,
                isCreditCardSelected: false,
                shouldApplyOnAccountPayment: true
            });
        } else {
            props.context.actionContext.update(new PaymentStateShareInput(), {
                isStoreSelected: false,
                isCODSelected: true,
                isCreditCardSelected: false,
                shouldApplyOnAccountPayment: true
            });
        }
    }, [props.data.storeSelectorStateManager?.result?.preferredStore]);
    const viewport = isMobile({ variant: VariantType.Browser, context: context.request });

    const isDesktopView = viewport === 'md' || viewport === 'lg' || viewport === 'xl';

    const closeDialogue = async (): Promise<void> => {
        const {
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = props;
        if (props.context.request.user.isSignedIn) {
            const userWithStore = JSON.parse(localStorage.getItem('logged_in_user_with_store') || '{}');
            if (
                storeSelectorStateManager &&
                userWithStore?.store?.StoreId &&
                props.context.request.user.customerAccountNumber &&
                !isUpdatingCartLines
            ) {
                storeSelectorStateManager.context?.parentElementRef?.current?.focus();
                return storeSelectorStateManager.closeDialog();
            }
        } else {
            if (storeSelectorStateManager && !isUpdatingCartLines) {
                storeSelectorStateManager.context?.parentElementRef?.current?.focus();
                return storeSelectorStateManager.closeDialog();
            }
        }
        return undefined;
    };

    const updatedModuleProps = {
        ...ModuleProps,
        toggle: closeDialogue,
        onClosed: closeDialogue
    };

    const updatedHeaderContainerProps = {
        ...HeaderContainerProps,
        toggle: closeDialogue
    };
    React.useEffect(() => {
        // @ts-ignore
        if (updatedModuleProps?.isOpen) {
            const seeAllButton = storeContainerRef.current?.querySelector(
                '.ms-store-select__search-see-all-stores'
            ) as HTMLDivElement | null;

            if (seeAllButton && !fetched) {
                const timer = setTimeout(() => {
                    seeAllButton.click();
                    setFetched(true);
                }, 1000);

                // Always return a cleanup function
                return () => clearTimeout(timer);
            }
        } else {
            setFetched(false);
        }

        // Return noop cleanup to satisfy TS
        return () => {};
    }, [updatedModuleProps]);

    const isOOSBasedOnStore = async (productId: number, unit: string) => {
        const getProductUnitOfMeasureInput = new GetProductUnitOfMeasureInput(productId, {
            IncludeQuantityLimitRules: true
        });

        const result = await getProductUnitsOfMeasureAction(getProductUnitOfMeasureInput, props.context.actionContext);
        const preferredStore = JSON.parse(localStorage.getItem('_msdyn365__preferredStore_') || '');

        const isStoreAvailableInUOM = (extensionProperties: any[], selectedStore: string, unit: string): boolean => {
            const obj = extensionProperties?.find(data => data.Symbol === unit);
            const uomStoreProp = obj.ExtensionProperties?.find((prop: any) => prop.Key === 'UOMAvailableStores');

            if (!uomStoreProp?.Value?.StringValue || !selectedStore) {
                return false;
            }

            try {
                const stores: UOMAvailableStore[] = JSON.parse(uomStoreProp.Value.StringValue);

                return stores.some(store => store.PickupStore === selectedStore);
            } catch (error) {
                console.error('Failed to parse UOMAvailableStores JSON', error);
                return false;
            }
        };
        return isStoreAvailableInUOM(result ?? [], preferredStore?.StoreId ?? '', unit);
    };

    const updateCartLines = async (lineIds: string[], location: IStoreInfo) => {
        setIsUpdatingCartLines(true);
        const getAllStores = await getOrgUnitLocationsByArea(
            new GetOrgUnitLocationsByAreaInput(0, 0, 0, 0, true),
            props.context.actionContext
        );
        const store = getAllStores?.find(item => item.OrgUnitAvailability?.OrgUnitLocation?.OrgUnitNumber === location?.StoreId);
        if (store?.OrgUnitAvailability?.OrgUnitLocation && props.data.cart.result) {
            for (const cartLineId of lineIds) {
                const product = props.data.cart.result?.cart?.CartLines?.find(data => data.LineId === cartLineId);
                if (product?.ProductId && product.UnitOfMeasureSymbol) {
                    const isOOS = await isOOSBasedOnStore(product.ProductId, product.UnitOfMeasureSymbol);

                    if (isOOS) {
                        try {
                            await props.data.cart.result?.removeCartLines({ cartLineIds: [cartLineId] });
                        } catch (err) {
                            console.error(`Failed to update cartLineId: ${cartLineId}`, err);
                        }
                    } else {
                        try {
                            await props.data.cart.result
                                .updateCartLinePickupLocation({
                                    cartLineId,
                                    location: store.OrgUnitAvailability.OrgUnitLocation,
                                    deliveryMode: 'BOPIS'
                                })
                                .then(res => {
                                    if (res.status === 'FAILED') {
                                        props.data.cart.result?.removeCartLines({ cartLineIds: [cartLineId] });
                                    }
                                });
                            console.log(`Updated cartLineId: ${cartLineId}`);
                        } catch (error) {
                            console.error(`Failed to update cartLineId: ${cartLineId}`, error);
                        }
                    }
                }
            }
            setIsloadAfterUpdating(false);
            setIsUpdatingCartLines(true);
            window.location.reload();
        }
    };

    React.useEffect(() => {
        if (props.data.cart.result?.cart?.CartLines && props.data.cart.result?.cart?.CartLines.length > 0) {
            setIsloadAfterUpdating(true);
        }
        if (props.data.cart.result?.cart?.CartLines) {
            const cartLines = [...props.data.cart.result?.cart?.CartLines];
            if (cartLines && cartLines?.length > 0 && props.data?.cart?.result && props.data.storeSelectorStateManager?.result) {
                const cartLinesStoreId = cartLines[0]?.WarehouseId;
                const hasSameStore = cartLines.every(line => line.WarehouseId === cartLinesStoreId);
                const selectedStore = props.data.storeSelectorStateManager?.result.preferredStore;
                if (cartLinesStoreId && selectedStore?.StoreId && (cartLinesStoreId !== selectedStore.StoreId || !hasSameStore)) {
                    const lineIds = cartLines.filter(line => typeof line.LineId === 'string').map(line => line.LineId) as string[];
                    updateCartLines(lineIds, selectedStore as IStoreInfo);
                }
            }
        }
    }, [props.data?.storeSelectorStateManager?.result?.preferredStore?.StoreId]);

    if (!isDesktopView || !isDesktopPort) {
        return (
            <Module {...updatedModuleProps}>
                {isUpdatingCartLines && <div className='page-overlay' />}
                <Node {...updatedHeaderContainerProps}>{HeaderProps}</Node>
                <Node {...BodyContainerProps}>
                    <Node ref={storeContainerRef} {...storeResultContainerProps!}>
                        {locationsMessage && (
                            <StoreSelectorFoundLocationsMessageComponent {...(locationsMessage as React.ReactElement).props} />
                        )}
                        <StoreImageComponent
                            storeId={props?.data?.storeSelectorStateManager?.result?.preferredStore?.StoreId || ''}
                            imageUrl={config.imageUrl}
                            imageUrl2={config.imageUrl2}
                            wrapperClass={'__mb-image'}
                        />
                        {search}
                        {props.data.cart?.result?.cart?.CartLines && props.data.cart?.result?.cart?.CartLines?.length > 0 && (
                            <div className='ms-store-select__switch-message '>
                                <p>
                                    <span>{resources.pleaseBeAdvised} </span>
                                    {config.switchingAccountWarningMessage ?? resources.switchingAccount}
                                </p>
                            </div>
                        )}
                        <Node {...bodyWrapperProps!}>
                            <Node {...storeResultContainerProps!}>
                                {state.isSearchInProgress ? (
                                    spinner
                                ) : (
                                    <>
                                        {locationsList
                                            ? renderStoreLocations(props, selectedLocationReference, reloadAfterUpdating)
                                            : noLocationsMessage}
                                    </>
                                )}
                                <Node
                                    className={`ms-store-select__map_view ${
                                        data.storeSelectorStateManager.result?.listMapViewState.displayMap ? '' : 'hide'
                                    }`}
                                >
                                    {maps}
                                </Node>
                                <Node {...storeDetailsModal!}>{selectedLocation && renderSelectedStore(selectedLocation, props)}</Node>
                            </Node>
                        </Node>
                    </Node>
                </Node>
                <Node {...FooterContainerProps}>{terms}</Node>
            </Module>
        );
    }
    return (
        <Module {...updatedModuleProps}>
            {isUpdatingCartLines && <div className='page-overlay' />}
            <Node {...updatedHeaderContainerProps}>{HeaderProps}</Node>
            <Node {...BodyContainerProps}>
                <Node
                    className={`ms-store-select__store_container ${
                        data.storeSelectorStateManager.result?.listMapViewState.displayList ? 'show' : ''
                    }`}
                    ref={storeContainerRef}
                >
                    {locationsMessage && (
                        <StoreSelectorFoundLocationsMessageComponent {...(locationsMessage as React.ReactElement).props} />
                    )}
                    <StoreImageComponent
                        storeId={props?.data?.storeSelectorStateManager?.result?.preferredStore?.StoreId || ''}
                        imageUrl={config.imageUrl}
                        imageUrl2={config.imageUrl2}
                        wrapperClass={'__image'}
                    />

                    {search}
                    {props.data.cart?.result?.cart?.CartLines && props.data.cart?.result?.cart?.CartLines?.length > 0 && (
                        <div className='ms-store-select__switch-message '>
                            <p>
                                <span>{resources.pleaseBeAdvised} </span>
                                {config.switchingAccountWarningMessage ?? resources.switchingAccount}
                            </p>
                        </div>
                    )}

                    {state.isSearchInProgress ? (
                        spinner
                    ) : (
                        <>
                            {locationsList
                                ? renderStoreLocations(props, selectedLocationReference, reloadAfterUpdating)
                                : noLocationsMessage}
                        </>
                    )}
                </Node>
                <Node
                    className={`ms-store-select__map_container ${
                        data.storeSelectorStateManager.result?.listMapViewState.displayMap ? '' : 'hide'
                    }`}
                >
                    {maps}
                </Node>
            </Node>
            <Node {...FooterContainerProps}>{terms}</Node>
        </Module>
    );
};

export default StoreSelectorViewComponent;

interface StoreImageComponentProps {
    storeId: string;
    imageUrl?: string;
    imageUrl2?: string;
    wrapperClass: string;
}

const StoreImageComponent: React.FC<StoreImageComponentProps> = ({ storeId, imageUrl, imageUrl2, wrapperClass }) => {
    const [currentStoreId, setCurrentStoreId] = React.useState(storeId);
    const [isTransitioning, setIsTransitioning] = React.useState(false);

    React.useEffect(() => {
        if (storeId !== currentStoreId) {
            setIsTransitioning(true);

            const timeout = setTimeout(() => {
                setCurrentStoreId(storeId);
                setIsTransitioning(false);
            }, 300);

            return () => clearTimeout(timeout);
        }
        return undefined;
    }, [storeId, currentStoreId]);

    return (
        <div className={`ms-store-select${wrapperClass}`}>
            {isTransitioning ? (
                <p className={'loading-text'}>Loading store image…</p>
            ) : currentStoreId === 'R001' && imageUrl ? (
                <img src={imageUrl} alt='Store R001' className='img1' />
            ) : currentStoreId === 'R002' && imageUrl2 ? (
                <img src={imageUrl2} alt='Store R002' className='img2' />
            ) : null}
        </div>
    );
};
